import Axios from 'axios';
import React from 'react';
import { useParams } from 'react-router-dom';
import WFDLogo from '../../components/Logo';
import {
  useGetStipulations,
  useUploadFileAndUpdateStipulation,
} from '../../hooks/useStipulations';
import { Stipulation, StipulationStatus } from '../../types/stipulations';
import { formatDate } from '../../utils/functions';
import LoadingFullScreen from '../../components/LoadingFullScreen';
import ErrorFullScreen from '../../components/ErrorFullScreen';
import LoadingIcon from '../../components/Loading';
import SuccessIcon from '../../components/Success';
import ErrorIcon from '../../components/Error';
import BaselineAccessTime from '../../components/BaselineAccessTime';
import Tooltip from '../../components/Tooltip';

const UploadPage = () => {
  const params = useParams<{
    dealId: string;
    type: 'applicant' | 'coApplicant';
  }>();
  const dealId = params.dealId;
  const type = params?.type;
  const {
    data,
    isLoading,
    error: getStipulationsError,
  } = useGetStipulations(
    { query: { dealId, type } },
    dealId !== undefined && type !== undefined
  );

  React.useEffect(() => {
    document.title = 'Upload application files';
  }, []);
  const [notifyViewedSend, setNotifyViewedSend] = React.useState(false);

  const notifyViewedStipulations = async (stipulations: Stipulation[]) => {
    for (let i = 0; i < stipulations.length; i++) {
      const stipulation = stipulations[i];
      try {
        await Axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/stipulation/${stipulation._id}/viewed`
        );
      } catch (error) {
        console.error('Error notifying viewed stipulation', error);
      }
    }
  };

  React.useEffect(() => {
    if (notifyViewedSend) return;
    if (isLoading || !data?.stipulations) return;
    setNotifyViewedSend(true);
    notifyViewedStipulations(data?.stipulations);
  }, [isLoading, notifyViewedSend, data?.stipulations]);

  if (getStipulationsError) {
    return <ErrorFullScreen />;
  }
  if (isLoading || !data?.stipulations || !data.names) {
    return <LoadingFullScreen />;
  }
  return (
    <div className="flex items-center lg:w-1/2 xl:w-1/3 m-auto  p-10 h-screen  flex-col gap-y-5">
      <div>
        <WFDLogo />
      </div>
      <div className=" w-full mb-10 flex flex-wrap gap-3 sm:gap-5 rounded-md bg-[#F5F7FB] py-6 px-8">
        <div className="flex flex-col gap-y-2">
          <div className="font-medium text-xl text-left">
            Hello {data.names},
          </div>
          <div className="font-medium text-xl text-left">
            In order to process your loan application we need you to upload the
            following documents:
          </div>
          <div className="text-sm text-gray-500">
            For security reasons, you cannot view the uploaded files. This
            ensures the confidentiality and integrity of your sensitive
            information.
          </div>
        </div>
      </div>
      {data.stipulations
        .filter((x) => x.data.info.description)
        .map((stipulation, index) => {
          return (
            <UploadFileCard
              uploaderType={type}
              key={index}
              stipulation={stipulation}
            />
          );
        })}
    </div>
  );
};
export default UploadPage;

type UploadFileCardProps = {
  stipulation: Stipulation;
  uploaderType: 'applicant' | 'coApplicant';
};
const UploadFileCard = ({ stipulation, uploaderType }: UploadFileCardProps) => {
  const inputFile = React.useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );
  const [error, setError] = React.useState(false);
  const {
    uploadFileAndUpdateStipulation,
    success,
    error: uploadError,
    loading,
  } = useUploadFileAndUpdateStipulation(() => setError(false));
  React.useEffect(() => {
    if (uploadError) {
      setErrorMessage('Something went wrong, please try again.');
      setError(true);
    } else {
      setErrorMessage(undefined);
      setError(false);
    }
  }, [uploadError]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files && e?.target?.files?.length) {
      const file = e.target.files[0];
      if (['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) {
        uploadFileAndUpdateStipulation({
          stipulationId: stipulation._id,
          uploaderType,
          file: file,
        });
      } else {
        setErrorMessage(
          'Please upload a valid file - supported types are JPG, PNG, PDF'
        );
        setError(true);
      }
    }
  };

  return (
    <div className="w-full  flex  flex-wrap items-center gap-2 sm:gap-5 rounded-md bg-[#F5F7FB] py-6 px-8 pr-2">
      <div className=" w-full flex  items-center justify-between gap-x-2 ">
        <div className="flex flex-col gap-y-2">
          <span className="font-medium text-lg">
            {stipulation.data.info.description}
          </span>
          {stipulation?.data?.info?.uploadedAt ? (
            <span className="font-normal text-sm text-gray-500">
              Uploaded at:{' '}
              {formatDate(stipulation.data.info.uploadedAt, 'medium', true)}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-y-2 items-center h-16 justify-center min-w-[140px]">
          {stipulation?.data?.info?.status === StipulationStatus.APPROVED ? (
            <SuccessIcon />
          ) : loading ? (
            <LoadingIcon />
          ) : stipulation?.data?.info?.status === StipulationStatus.PENDING ? (
            <Tooltip text="Your file is being processed by the F&I team. In case of any issues, you will be notified.">
              <BaselineAccessTime />
            </Tooltip>
          ) : error ? (
            <ErrorIcon />
          ) : null}
          {!loading &&
            !error &&
            stipulation?.data?.info?.status !== StipulationStatus.APPROVED && (
              <div className="h-16 flex items-center ">
                <input
                  onClick={(e) => {
                    (e.target as EventTarget & { value: null }).value = null;
                  }}
                  onChange={(e) => {
                    handleFileChange(e);
                  }}
                  ref={inputFile}
                  type="file"
                  id={`upload_file_${stipulation._id}`}
                  hidden
                />
                <label
                  onClick={() => inputFile?.current?.click()}
                  htmlFor="upload_file"
                  className="py-2 px-3 inline-flex min-w-[131px] justify-center cursor-pointer items-center gap-x-2 text-xs font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                >
                  <svg
                    className="shrink-0 size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" x2="12" y1="3" y2="15"></line>
                  </svg>
                  {stipulation?.data?.info?.url === 'not_uploaded'
                    ? 'Upload file'
                    : 'Re-upload file'}
                </label>
              </div>
            )}
        </div>
      </div>
      {error && errorMessage && !success && !loading && (
        <div className="flex flex-col text-xs text-red-600  text-center  w-full">
          {errorMessage}
        </div>
      )}
    </div>
  );
};
